/* Drawer chrome + all read drawers: cell (trace-backed "why"), scenario,
   no-regret (live optimizer job), most-likely, blind spots, capability
   modal, entity editor. */
import React, { useEffect, useMemo, useState } from 'react';
import type { Strategy } from '@flourish/schema';
import { LEVELS3, type CompanyProfile } from '@flourish/schema';
import { I, initials, pctText, JobSteps } from '../ui.js';
import { useStore, activeEntity, hiddenScenarios, type DrawerState } from '../store.jsx';
import {
  api, type EntityView, type ScenarioView, type JobView, type EvidenceView, type SuggestionView,
} from '../api.js';
import {
  cell, trace, chartSeries, overallSuccess, toWeights, evaluatePortfolio, focusedScenarios,
  type ScenarioId,
} from '../local.js';

/* ---------- chrome ---------- */
export function Drawer({ open, onClose, eyebrow, title, titleColor, foot, wide, children }: {
  open: boolean; onClose: () => void;
  eyebrow?: React.ReactNode; title?: React.ReactNode; titleColor?: string;
  foot?: React.ReactNode; wide?: boolean; children?: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <div className={`scrim ${open ? 'open' : ''}`} onClick={onClose} />
      <aside className={`drawer ${open ? 'open' : ''} ${wide ? 'wide' : ''}`} role="dialog" aria-modal>
        {open && (
          <>
            <div className="drawer-head">
              <div className="dh-main">
                <div className="eyebrow">{eyebrow}</div>
                <h3 style={titleColor ? { color: titleColor } : undefined}>{title}</h3>
              </div>
              <button className="x-btn" onClick={onClose}><I k="close" /></button>
            </div>
            <div className="drawer-body">{children}</div>
            {foot && <div className="drawer-foot" style={{ display: 'flex' }}>{foot}</div>}
          </>
        )}
      </aside>
    </>
  );
}

const scnColor = (s: ScenarioView) =>
  ['bau1', 'bau2', 'unlim', 'crop', 'delayed'].includes(s.id) ? 'var(--bad)'
    : s.id === 'sw' ? 'var(--good)' : s.prob <= 0 ? 'var(--ink-3)' : 'var(--accent)';

/* ---------- trace waterfall ---------- */
const DRIVER_LABELS: [string, string][] = [
  ['demand', 'World demand (sector mix)'],
  ['inputScarcity', 'Input scarcity & extraction cost'],
  ['pollutionBurden', 'Pollution & regulatory burden'],
  ['hazardShocks', 'Turbulence & shocks'],
  ['transitionOpportunity', 'Transition opportunity'],
  ['strategyEffects', 'Your strategy'],
  ['interaction', 'Interaction effects'],
];

function Waterfall({ drivers }: { drivers: Record<string, number> }) {
  const max = Math.max(4, ...DRIVER_LABELS.map(([k]) => Math.abs(drivers[k] ?? 0)));
  const total = DRIVER_LABELS.reduce((s, [k]) => s + (drivers[k] ?? 0), 0);
  const bar = (v: number) => {
    const w = Math.min(50, (Math.abs(v) / max) * 50);
    return <div className="wf-track"><div className="zero" /><div className={`wf-bar ${v >= 0 ? 'pos' : 'neg'}`} style={{ width: `${w}%` }} /></div>;
  };
  return (
    <div className="wf">
      {DRIVER_LABELS.map(([k, label]) => (
        <div className="wf-row" key={k}>
          <span className="lbl">{label}</span>
          {bar(drivers[k] ?? 0)}
          <span className="val mono" style={{ color: (drivers[k] ?? 0) >= 0 ? 'var(--good)' : 'var(--bad)' }}>
            {(drivers[k] ?? 0) > 0 ? '+' : ''}{(drivers[k] ?? 0).toFixed(1)}
          </span>
        </div>
      ))}
      <div className="wf-row total">
        <span className="lbl">Net change vs today</span>
        {bar(total)}
        <span className="val mono">{total > 0 ? '+' : ''}{total.toFixed(1)}</span>
      </div>
    </div>
  );
}

/* ---------- cell drawer (strategy x scenario AND intervention x scenario) ---------- */
export function CellDrawer({ d }: { d: Extract<DrawerState, { kind: 'cell' } | { kind: 'iv-cell' }> }) {
  const { s, set } = useStore();
  const e = activeEntity(s)!;
  const scn = s.scenarios.find(x => x.id === d.scenarioId)!;
  const base = (s.strategiesByEntity[e.id] ?? []).find(x => x.id === d.strategyId)!;

  const strategy: Strategy = d.kind === 'iv-cell'
    ? { ...base, id: `${base.id}~${d.iid}`, interventions: base.interventions.filter(iv => iv.iid === d.iid) }
    : base;
  const ivName = d.kind === 'iv-cell' ? base.interventions.find(iv => iv.iid === d.iid)?.name : null;

  const r = cell(e, strategy, scn.id as ScenarioId);
  const t = trace(e, strategy, scn.id as ScenarioId);

  const [narration, setNarration] = useState<{ prose: string; source: string } | null>(null);
  const [narrating, setNarrating] = useState(false);
  const narrate = async () => {
    if (d.kind === 'iv-cell') return;   // narration covers the full-portfolio cell
    setNarrating(true);
    try { setNarration(await api.narrate(e.id, base.id, scn.id)); }
    catch { setNarration(null); }
    setNarrating(false);
  };

  return (
    <Drawer open onClose={() => set({ drawer: null })}
      eyebrow={<>{ivName ? `${ivName} · within ${base.name}` : base.name} · <span style={{ color: scnColor(scn) }}>{scn.name}</span></>}
      title={`Why this cell — ${e.name}`}
      foot={
        <>
          <button className="btn" onClick={() => set({ drawer: { kind: 'scenario', scenarioId: scn.id } })}><I k="info" /> Scenario detail</button>
          {!base.isNoRegret && !base.isMostLikely && (
            <button className="btn" onClick={() => set({ drawer: { kind: 'strategy-editor', strategyId: base.id } })}><I k="edit" /> Edit strategy</button>
          )}
          <div className="grow" />
          <button className="btn primary" onClick={() => set({ drawer: null })}>Done</button>
        </>
      }>
      <p className="prose" style={{ marginTop: -4 }}>{scn.tagline} Every number below is deterministic engine output — the same trace the server stores.</p>

      <div className="cell-break">
        {r.horizons.map(h => (
          h.death
            ? <div key={h.years} className="tile death"><span className="tf">{h.years}y</span><span className="tsym"><I k="skull" /></span></div>
            : <div key={h.years} className={`tile ${h.pct >= 2 ? 'up' : h.pct <= -2 ? 'down' : 'flat'}`}>
                <span className="tf">{h.years}y</span><span className="tpct mono" style={{ fontSize: 15 }}>{pctText(h.pct)}</span>
              </div>
        ))}
      </div>
      {r.deathYear && (
        <div className="callout warn" style={{ marginTop: 12 }}>
          <div className="ico"><I k="skull" /></div>
          <div><strong>Does not survive this world</strong> — the organisation fails in <strong>{r.deathYear}</strong>.</div>
        </div>
      )}

      {d.kind === 'cell' && (
        <div style={{ marginTop: 10 }}>
          {narration
            ? <div className="callout info"><div className="ico"><I k="wand" /></div>
                <div><em>{narration.prose}</em>
                  <div className="muted" style={{ fontSize: 10.5, marginTop: 4 }}>
                    {narration.source === 'deterministic' ? 'deterministic summary (no language model configured)' : `narrated by ${narration.source} — numbers validated against the trace`}
                  </div></div></div>
            : <button className="btn sm" disabled={narrating} onClick={() => void narrate()}>
                <I k="wand" /> {narrating ? 'Narrating…' : 'Narrate this cell'}
              </button>}
        </div>
      )}

      <div className="section-h">What drives it — contribution to the change vs today</div>
      <Waterfall drivers={t.drivers as unknown as Record<string, number>} />

      {t.interventions.length > 0 && (
        <>
          <div className="section-h">Interventions in this world — leave-one-out</div>
          <div className="mgl">
            {t.interventions.map(iv => (
              <div className="mgl-row" key={iv.iid}>
                <span className={`iv-dir ${iv.marginal >= 0 ? 'pos' : 'neg'}`}><I k={iv.marginal >= 0 ? 'arrowUp' : 'arrowDown'} /></span>
                <span className="nm">{iv.name}{iv.note && <div className="note">{iv.note}</div>}</span>
                <span className="note mono">{Math.round(iv.rampedShare * 100)}% ramped</span>
                <span className={`val ${iv.marginal >= 0 ? 'pos' : 'neg'}`}>{iv.marginal > 0 ? '+' : ''}{iv.marginal.toFixed(1)}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {t.events.length > 0 && (
        <>
          <div className="section-h">Timeline</div>
          <div className="evt-list">
            {t.events.map((ev, i) => (
              <div className={`evt ${ev.kind === 'death' ? 'death' : ''}`} key={i}>
                <span className="yr mono">{ev.year}</span><span>{ev.detail}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="section-h">Why this world behaves this way</div>
      <p className="prose">{scn.mechanism} <strong>{scn.failure}</strong></p>
      <div className="def-list" style={{ marginTop: 12 }}>
        <div className="def-row"><span className="dt">Scenario probability</span><span className="dd mono">{scn.prob}%</span></div>
        <div className="def-row"><span className="dt">Engine</span><span className="dd mono">{t.engineVersion} · {t.inputsHash}</span></div>
      </div>
    </Drawer>
  );
}

/* ---------- scenario drawer with the real 1900-2100 chart ---------- */
function VariablesChart({ scenarioId }: { scenarioId: string }) {
  const data = useMemo(() => chartSeries(scenarioId as ScenarioId), [scenarioId]);
  const W = 660, H = 250, pl = 6, pr = 6, pt = 12, pb = 24, IW = W - pl - pr, IH = H - pt - pb;
  const xs = (x: number) => pl + x * IW;
  const ys = (v: number) => pt + IH - Math.max(0, Math.min(1, v)) * IH;
  const series: [keyof typeof data[0] & string, string, string][] = [
    ['pop', 'Population', 'var(--accent)'], ['ind', 'Industrial output', 'var(--bad)'],
    ['food', 'Food', 'var(--thrive)'], ['poll', 'Pollution', '#8b5cf6'], ['res', 'Resources', 'var(--good)'],
  ];
  const line = (k: string) => data.map((p, i) =>
    `${i ? 'L' : 'M'}${xs(p.x).toFixed(1)} ${ys((p as never)[k]).toFixed(1)}`).join(' ');
  const grid: React.ReactNode[] = [];
  for (let yr = 1900; yr <= 2100; yr += 50) {
    const gx = xs((yr - 1900) / 200);
    grid.push(<line key={yr} x1={gx} y1={pt} x2={gx} y2={pt + IH} stroke="var(--line)" strokeWidth={1} />);
    grid.push(<text key={`t${yr}`} x={gx} y={H - 8} textAnchor="middle" fontSize={9} fill="var(--ink-3)">{yr}</text>);
  }
  const tx = xs((2026 - 1900) / 200);
  return (
    <div className="chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`}>
        {grid}
        <line x1={tx} y1={pt} x2={tx} y2={pt + IH} stroke="var(--ink-2)" strokeWidth={1.2} strokeDasharray="3 3" />
        <text x={tx} y={pt - 2} textAnchor="middle" fontSize={9} fontWeight={700} fill="var(--ink-2)">now</text>
        {series.map(([k, , c]) => <path key={k} d={line(k)} fill="none" stroke={c} strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" />)}
      </svg>
      <div className="legend">{series.map(([, l, c]) => <div className="lg" key={l}><span className="sw" style={{ background: c }} />{l}</div>)}</div>
    </div>
  );
}

export function ScenarioDrawer({ d }: { d: Extract<DrawerState, { kind: 'scenario' }> }) {
  const { s, set } = useStore();
  const scn = s.scenarios.find(x => x.id === d.scenarioId)!;
  const [prob, setProb] = useState(scn.prob);
  useEffect(() => setProb(scn.prob), [scn.prob]);
  const obsolete = scn.defaultProb <= 0;
  const col = scnColor(scn);
  return (
    <Drawer open onClose={() => set({ drawer: null })}
      eyebrow={<>{scn.cat} · {scn.full}</>} title={scn.name} titleColor={col}
      foot={<><div className="muted" style={{ fontSize: 11.5 }}>Source: Limits to Growth · World3 (1972) · simulated live in your browser</div><div className="grow" /><button className="btn primary" onClick={() => set({ drawer: null })}>Done</button></>}>
      <div className="row" style={{ alignItems: 'center', gap: 14, marginBottom: 16 }}>
        <div className="mono" style={{ fontSize: 40, fontWeight: 760, letterSpacing: '-.04em', color: col }}>{prob}<span style={{ fontSize: 18 }}>%</span></div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 600 }}>Probability this world defines the trajectory</div>
          {!obsolete && (
            <input type="range" min={1} max={99} value={prob} style={{ width: '100%' }}
              onChange={ev => setProb(Number(ev.target.value))}
              onMouseUp={() => void api.patchScenario(scn.id, { prob })}
              onTouchEnd={() => void api.patchScenario(scn.id, { prob })} />
          )}
          <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 6 }}>
            Peak / inflection: <strong style={{ color: 'var(--ink-2)' }}>{scn.peak}</strong>
            {scn.prob !== scn.defaultProb && <> · research default {scn.defaultProb}% (edited, audited)</>}
          </div>
        </div>
      </div>
      <p className="prose serif" style={{ fontSize: 15, color: 'var(--ink)' }}>{scn.tagline}</p>

      <div className="section-h">Focus &amp; visibility</div>
      <div className="foc-toggle">
        <button className={`foc-opt ${scn.focus && !scn.hidden ? 'sel' : ''} ${obsolete ? 'disabled' : ''}`}
          onClick={obsolete ? undefined : () => void api.patchScenario(scn.id, { focus: !scn.focus })}>
          <I k={scn.focus ? 'check2' : 'square'} /><span>{scn.focus ? 'In focus' : 'Add to focus'}</span>
          <small>optimise the no-regret strategy for this world</small>
        </button>
        <button className={`foc-opt ${scn.hidden ? 'sel hide' : ''}`}
          onClick={() => void api.patchScenario(scn.id, { hidden: !scn.hidden })}>
          <I k={scn.hidden ? 'eye' : 'eyeOff'} /><span>{scn.hidden ? 'Hidden' : 'Hide'}</span>
          <small>{scn.hidden ? 'shown in blind spots' : 'move to blind spots'}</small>
        </button>
      </div>

      <div className="section-h"><I k="chart" /> All variables · 1900–2100 — live World3 run</div>
      <VariablesChart scenarioId={scn.id} />

      <div className="section-h">Dominant mechanism</div><p className="prose">{scn.mechanism}</p>
      <div className="section-h">Primary failure mode</div><p className="prose">{scn.failure}</p>
      <div className="section-h">Outcome by end of century</div><p className="prose">{scn.outcome}</p>
      <div className="callout info" style={{ marginTop: 14 }}><div className="ico"><I k="info" /></div><div><strong>2026 probability read.</strong> {scn.rationale}</div></div>
      {obsolete && <div className="callout warn" style={{ marginTop: 12 }}><div className="ico"><I k="clock" /></div><div>This run required action in 1975. It is <strong>not modeled</strong> in the matrix (0% probability) — shown for completeness.</div></div>}
    </Drawer>
  );
}

/* ---------- no-regret drawer with the real optimizer job ---------- */
export function NoRegretDrawer({ d }: { d: Extract<DrawerState, { kind: 'no-regret' }> }) {
  const { s, set, toast } = useStore();
  const e = activeEntity(s)!;
  const st = (s.strategiesByEntity[e.id] ?? []).find(x => x.id === d.strategyId);
  const [jobId, setJobId] = useState<string | null>(null);
  const [result, setResult] = useState<{ trace: { kind: string; detail: string }[]; eval: { value: number; deaths: number } } | null>(null);
  const job: JobView | undefined = jobId ? s.jobs[jobId] : undefined;

  useEffect(() => {
    if (job?.state === 'done' && job.resultRef) {
      void api.run(job.resultRef).then(r => setResult(r.payload as never));
    }
  }, [job?.state, job?.resultRef]);

  if (!st) return null;
  const focused = focusedScenarios(toWeights(s.scenarios));
  const ev = evaluatePortfolio(e, st.interventions, focused);
  const hid = hiddenScenarios(s);

  const regenerate = async () => {
    setResult(null);
    const res = await api.optimize(e.id).catch((err: Error) => { toast(err.message); return null; });
    if (res) setJobId(res.jobId);
  };

  return (
    <Drawer open onClose={() => set({ drawer: null })}
      eyebrow={<><I k="wand" /> Auto-composed by the optimizer</>}
      title="No-Regret Strategy" titleColor="var(--accent)"
      foot={<><div className="muted" style={{ fontSize: 11.5 }}>Recomputes from your focused worlds &amp; live probabilities</div><div className="grow" />
        <button className="btn primary" onClick={() => void regenerate()} disabled={job?.state === 'running'}>
          <I k="wand" /> {job?.state === 'running' ? 'Optimizing…' : 'Regenerate'}
        </button></>}>
      <p className="prose serif" style={{ fontSize: 15, marginTop: -4 }}>{st.desc}</p>
      <div className="mini-metrics" style={{ marginTop: 8 }}>
        <div className="mm"><span className="v mono">{st.interventions.length}</span><span className="l"><I k="layers" />Interventions</span></div>
        <div className="mm"><span className="v mono" style={{ color: ev.deaths === 0 ? 'var(--good)' : 'var(--bad)' }}>{focused.length - ev.deaths}/{focused.length}</span><span className="l"><I k="shield" />Worlds survived</span></div>
        <div className="mm"><span className="v mono" style={{ color: 'var(--accent)' }}>{overallSuccess(e, st, toWeights(s.scenarios))}</span><span className="l"><I k="target" />@50y</span></div>
      </div>

      <div className="section-h">Optimising for {focused.length} focused worlds</div>
      <div className="chips">{focused.map(f => {
        const scn = s.scenarios.find(x => x.id === f.id)!;
        return <span className="chip" key={f.id}>{scn.name} · {scn.prob}%</span>;
      })}</div>
      {hid.length > 0 && (
        <div className="callout warn" style={{ marginTop: 10 }}><div className="ico"><I k="eyeOff" /></div>
          <div><strong>{hid.length} blind spot{hid.length === 1 ? '' : 's'}</strong> excluded: {hid.map(x => x.name).join(', ')}. The optimizer is <em>not</em> hedging these.</div></div>
      )}

      {job && job.state !== 'done' && (
        <>
          <div className="section-h">Optimizer running</div>
          <JobSteps steps={job.steps} error={job.error} />
        </>
      )}

      {result && (
        <>
          <div className="section-h">How it was composed — the real search</div>
          <div className="reason-list">
            {result.trace.filter(t => t.kind !== 'assign-priorities').map((t, i) => (
              <div className="reason show" key={i}><span className="rn">{i + 1}</span><span>{t.detail}</span></div>
            ))}
          </div>
        </>
      )}

      <div className="section-h">Interventions</div>
      <div className="iv-readlist">
        {st.interventions.map(iv => (
          <div className="iv-read" key={iv.iid}>
            <span className="iv-dot" />
            <div className="iv-main">
              <div className="iv-name">{iv.name}</div>
              <div className="iv-meta"><span className="iv-owner"><I k="owner" />{iv.owner || '—'}</span><span className={`prio p-${iv.priority}`}>{iv.priority}</span></div>
            </div>
          </div>
        ))}
      </div>
    </Drawer>
  );
}

/* ---------- most-likely: evidence-based inference with citations,
   curated placeholder until sources + an LLM are available ---------- */
export function MostLikelyDrawer({ d }: { d: Extract<DrawerState, { kind: 'most-likely' }> }) {
  const { s, set, toast, refreshStrategies } = useStore();
  const o = s.entities.find(x => x.id === d.entityId);
  const ml = o ? (s.strategiesByEntity[o.id] ?? []).find(x => x.isMostLikely) : undefined;
  const [jobId, setJobId] = useState<string | null>(null);
  const job: JobView | undefined = jobId ? s.jobs[jobId] : undefined;

  useEffect(() => {
    if (job?.state === 'done' && o) void refreshStrategies(o.id);
  }, [job?.state, o, refreshStrategies]);

  if (!o || !ml) return null;
  const meta = ml.meta;

  const refresh = async () => {
    try { setJobId((await api.inferMostLikely(o.id)).jobId); }
    catch (err) { toast((err as Error).message); }
  };

  return (
    <Drawer open onClose={() => set({ drawer: null })}
      eyebrow={<><I k="globe" /> Inferred play</>} title="Most Likely Strategy" titleColor={o.accent}
      foot={<>
        <div className="muted" style={{ fontSize: 11.5 }}>
          {meta ? <>inferred by {meta.model} · {new Date(meta.inferredAt).toLocaleDateString()} · confidence {(meta.confidence * 100).toFixed(0)}%</>
            : 'add public sources in the entity editor, then refresh'}
        </div>
        <div className="grow" />
        <button className="btn primary" disabled={job?.state === 'running'} onClick={() => void refresh()}>
          <I k="globe" /> {job?.state === 'running' ? 'Inferring…' : 'Refresh inference'}
        </button>
      </>}>
      <div className="row" style={{ alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span className="ent-avatar" style={{ background: o.accent, width: 30, height: 30, borderRadius: 8 }}>{initials(o.name)}</span>
        <div><div style={{ fontWeight: 640 }}>{o.name}</div><div className="muted" style={{ fontSize: 11.5 }}>{o.type} · {o.profile.sector}</div></div>
      </div>
      <p className="prose serif" style={{ fontSize: 15 }}>{ml.desc}</p>

      {!meta && (
        <div className="callout warn" style={{ marginTop: 10 }}><div className="ico"><I k="info" /></div>
          <div><strong>Curated placeholder.</strong> Store public sources for {o.name} (entity editor → Public data), then refresh — the inference runs from evidence, with citations and confidence.</div></div>
      )}

      {job && job.state !== 'done' && (
        <>
          <div className="section-h">Inference running</div>
          <JobSteps steps={job.steps} error={job.error} />
        </>
      )}

      <div className="mini-metrics" style={{ marginTop: 10 }}>
        <div className="mm"><span className="v mono">{ml.interventions.length}</span><span className="l"><I k="layers" />Interventions</span></div>
        <div className="mm"><span className="v mono" style={{ color: o.accent }}>{overallSuccess(o, ml, toWeights(s.scenarios))}</span><span className="l"><I k="target" />@50y</span></div>
        {meta && <div className="mm"><span className="v mono">{(meta.confidence * 100).toFixed(0)}%</span><span className="l"><I k="globe" />Confidence</span></div>}
      </div>

      <div className="section-h">Likely interventions</div>
      <div className="iv-readlist">
        {ml.interventions.map(iv => {
          const citation = meta?.citations.find(c => c.name === iv.name);
          return (
            <div className="iv-read" key={iv.iid} style={{ alignItems: 'flex-start' }}>
              <span className="iv-dot" style={{ marginTop: 6 }} />
              <div className="iv-main">
                <div className="iv-name">{iv.name}</div>
                <div className="iv-meta"><span className="iv-owner"><I k="owner" />{iv.owner || '—'}</span><span className={`prio p-${iv.priority}`}>{iv.priority}</span></div>
                {citation && <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>“{citation.quote}”</div>}
              </div>
            </div>
          );
        })}
      </div>

      {meta && meta.sources.length > 0 && (
        <>
          <div className="section-h">Sources</div>
          <div className="chips">{meta.sources.map(src => <span className="chip" key={src.id} title={src.ref}>{src.title || src.ref}</span>)}</div>
        </>
      )}
    </Drawer>
  );
}

/* ---------- blind spots ---------- */
export function BlindspotsDrawer() {
  const { s, set, toast } = useStore();
  const e = activeEntity(s)!;
  const nr = (s.strategiesByEntity[e.id] ?? []).find(x => x.isNoRegret) ?? (s.strategiesByEntity[e.id] ?? [])[0];
  const hid = hiddenScenarios(s);
  return (
    <Drawer open onClose={() => set({ drawer: null })}
      eyebrow={<><I k="eyeOff" /> Hidden · not optimised for</>} title="Blind spots"
      foot={<><div className="muted" style={{ fontSize: 11.5 }}>{hid.length} hidden scenario{hid.length === 1 ? '' : 's'}</div><div className="grow" /><button className="btn primary" onClick={() => set({ drawer: null })}>Done</button></>}>
      <p className="prose" style={{ marginTop: -4 }}>Hidden scenarios are excluded from the matrix and from the No-Regret optimizer. Keep an eye on the <strong style={{ color: 'var(--bad)' }}>deadly</strong> and <strong style={{ color: 'var(--good)' }}>high-upside</strong> ones you're choosing to ignore.</p>
      <div className="bs-rows">
        {hid.map(scn => {
          let flag = 'mute', txt = scn.prob > 0 ? `${scn.prob}% likely` : 'not modeled';
          if (scn.prob > 0 && nr) {
            const r = cell(e, nr, scn.id as ScenarioId);
            const last = r.horizons[3];
            if (last.death) { flag = 'bad'; txt = `deadly · ${scn.prob}% likely`; }
            else if (last.pct >= 20) { flag = 'good'; txt = `high upside · ${scn.prob}% likely`; }
          }
          return (
            <div className={`bs-row ${flag}`} key={scn.id}>
              <span className="bs-flagico"><I k={flag === 'bad' ? 'skull' : flag === 'good' ? 'arrowUp' : 'info'} /></span>
              <div className="bs-rmain">
                <div className="bs-rname">{scn.name}</div>
                <div className="bs-rmeta">{scn.cat} · {txt}</div>
              </div>
              <button className="btn sm" title="Scenario detail" onClick={() => set({ drawer: { kind: 'scenario', scenarioId: scn.id } })}><I k="info" /></button>
              <button className="btn sm primary" onClick={() => { void api.patchScenario(scn.id, { hidden: false }); toast(`${scn.name} back in the matrix`); }}>
                <I k="eye" /> Unhide
              </button>
            </div>
          );
        })}
      </div>
    </Drawer>
  );
}

/* ---------- capability modal (phase-gated entity types) ---------- */
export function CapabilityModal() {
  const { s, set } = useStore();
  const key = s.capabilityModal;
  if (!key || !s.capabilities) return null;
  const cap = s.capabilities.entityTypes[key];
  if (!cap) return null;
  return (
    <div className="modal-scrim" onClick={() => set({ capabilityModal: null })}>
      <div className="modal cap-modal" onClick={e => e.stopPropagation()}>
        <span className="cap-status">Coming in {cap.eta ?? 'a later version'}</span>
        <h3>{cap.label} entities</h3>
        <p>{cap.blurb}</p>
        <p style={{ marginTop: 8 }}>This first phase focuses on <strong>companies</strong> — including your competitors.</p>
        <div style={{ display: 'flex', marginTop: 16 }}>
          <div style={{ flex: 1 }} />
          <button className="btn primary" onClick={() => set({ capabilityModal: null })}>Understood</button>
        </div>
      </div>
    </div>
  );
}

/* ---------- public-data analysis: connectors -> enrich job -> review queue ---------- */
function PublicDataSection({ entity }: { entity: EntityView }) {
  const { s, toast, refresh } = useStore();
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [jobId, setJobId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<SuggestionView[]>([]);
  const [evidence, setEvidence] = useState<EvidenceView[]>([]);
  const job: JobView | undefined = jobId ? s.jobs[jobId] : undefined;

  const load = async () => {
    const [sg, ev] = await Promise.all([api.suggestions(entity.id), api.evidence(entity.id)]);
    setSuggestions(sg.suggestions);
    setEvidence(ev.evidence);
  };
  useEffect(() => { void load(); }, [entity.id]);           // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (job?.state === 'done') void load(); }, [job?.state]);  // eslint-disable-line react-hooks/exhaustive-deps

  const analyze = async () => {
    try {
      const res = await api.enrich(entity.id, {
        urls: url.trim() ? [url.trim()] : [],
        text: text.trim() || undefined,
      });
      setJobId(res.jobId);
      setText(''); setUrl('');
    } catch (err) { toast((err as Error).message); }
  };

  const review = async (field: string, action: 'accept' | 'reject') => {
    await api.reviewSuggestion(entity.id, field, action);
    await load();
    if (action === 'accept') await refresh();
    toast(action === 'accept' ? 'Applied — provenance recorded' : 'Suggestion dismissed');
  };

  return (
    <>
      <div className="section-h">Public data <span style={{ textTransform: 'none', color: 'var(--ink-3)', fontWeight: 400 }}>— enrichment proposes, you apply</span></div>
      <div className="field">
        <input className="input sm" value={url} placeholder="https:// source to fetch (annual report page, newsroom…)"
          onChange={ev => setUrl(ev.target.value)} />
      </div>
      <div className="field">
        <textarea className="textarea" value={text} style={{ minHeight: 64 }}
          placeholder="…or paste material directly (works fully offline / air-gapped)"
          onChange={ev => setText(ev.target.value)} />
      </div>
      <button className="btn" style={{ width: '100%', justifyContent: 'center' }}
        disabled={job?.state === 'running' || (!url.trim() && text.trim().length < 40)}
        onClick={() => void analyze()}>
        <I k="globe" /> {job?.state === 'running' ? 'Analyzing…' : 'Analyze public data'}
      </button>

      {job && <div style={{ marginTop: 10 }}><JobSteps steps={job.steps} error={job.error} /></div>}

      {suggestions.length > 0 && (
        <>
          <div className="section-h">Suggested from evidence <span style={{ textTransform: 'none', color: 'var(--ink-3)', fontWeight: 400 }}>— never applied without you</span></div>
          {suggestions.map(sg => (
            <div className="mgl-row" key={sg.field} style={{ alignItems: 'flex-start' }}>
              <div className="nm" style={{ whiteSpace: 'normal' }}>
                <strong>{sg.field}</strong> → {String(sg.value)}
                <span className="prov enriched" style={{ marginLeft: 6 }}>{Math.round(sg.confidence * 100)}%</span>
                {sg.evidence[0] && <div className="note" style={{ marginTop: 3 }}>“{sg.evidence[0].quote}”</div>}
              </div>
              <button className="btn sm primary" onClick={() => void review(sg.field, 'accept')}>Apply</button>
              <button className="btn sm" onClick={() => void review(sg.field, 'reject')}>Dismiss</button>
            </div>
          ))}
        </>
      )}

      {evidence.length > 0 && (
        <>
          <div className="section-h">Evidence on file</div>
          <div className="chips">
            {evidence.map(ev => (
              <span className="chip" key={ev.id} title={`${ev.ref} · ${ev.chars} chars · ${new Date(ev.retrievedAt).toLocaleDateString()}`}>
                {ev.kind === 'url' ? <I k="globe" /> : <I k="edit" />} {ev.title || ev.ref}
                <span className="chip-x" title="Delete evidence"
                  onClick={async () => { await api.deleteEvidence(ev.id); await load(); }}><I k="close" /></span>
              </span>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/* ---------- entity editor (companies; other types open the capability modal) ---------- */
export function EntityEditor({ d }: { d: Extract<DrawerState, { kind: 'entity-editor' }> }) {
  const { s, set, toast, refresh } = useStore();
  const existing = d.entityId ? s.entities.find(x => x.id === d.entityId) : null;
  const [name, setName] = useState(existing?.name ?? '');
  const [desc, setDesc] = useState(existing?.description ?? '');
  const [type, setType] = useState<string>(existing?.type ?? 'company');
  const [profile, setProfile] = useState<Partial<CompanyProfile>>(existing?.profile ?? {});

  const save = async () => {
    try {
      if (existing) {
        await api.updateEntity(existing.id, { name, description: desc });
        await api.updateProfile(existing.id, profile);
        toast('Entity updated');
      } else {
        await api.createEntity({ name: name || 'New Entity', type, description: desc, profile });
        toast('Entity added');
      }
      await refresh();
      set({ drawer: null });
    } catch (err) { toast((err as Error).message); }
  };
  const remove = async () => {
    if (!existing) return;
    try { await api.deleteEntity(existing.id); await refresh(); set({ drawer: null }); toast('Entity removed'); }
    catch (err) { toast((err as Error).message); }
  };

  const typeGrid = !existing && (
    <div className="field">
      <label>Entity type</label>
      <div className="type-grid">
        {(['company', 'government', 'ngo', 'individual'] as const).map(t => {
          const cap = s.capabilities?.entityTypes[t];
          const status = cap?.status ?? 'planned';
          const available = status === 'ga' || status === 'beta';
          return (
            <button key={t} className={`type-opt ${type === t ? 'sel' : ''}`}
              onClick={() => (available ? setType(t) : set({ capabilityModal: t }))}>
              <span className="ti"><I k={t === 'company' ? 'building' : t === 'government' ? 'bank' : t === 'ngo' ? 'hands' : 'user'} /></span>
              <span>
                <span className="tt">{t === 'ngo' ? 'NGO' : t[0].toUpperCase() + t.slice(1)}
                  {status === 'beta' && <span className="iv-badge sug" style={{ marginLeft: 6 }}>beta</span>}
                </span>
                <span className="td">{available ? (cap?.blurb.split(':')[0] ?? '') : 'Coming in a later version'}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const lvl = (field: 'resourceDep' | 'footprint' | 'adaptive', label: string, hint: string) => (
    <div className="def-row" key={field}>
      <span className="dt">{label}<div style={{ fontSize: 10, color: 'var(--ink-3)', textTransform: 'none', fontWeight: 400 }}>{hint}</div></span>
      <span className="dd">
        <select className="select sm" value={(profile[field] as string) ?? existing?.profile[field] ?? 'Medium'}
          onChange={ev => setProfile(p => ({ ...p, [field]: ev.target.value as never }))}>
          {LEVELS3.map(l => <option key={l}>{l}</option>)}
        </select>
        {existing?.provenance[field] && <span className={`prov ${existing.provenance[field].source}`} style={{ marginLeft: 8 }}>{existing.provenance[field].source}</span>}
      </span>
    </div>
  );

  return (
    <Drawer open onClose={() => set({ drawer: null })}
      eyebrow={existing ? 'Edit entity' : 'New entity to compare'}
      title={existing ? existing.name : 'Define a company'}
      foot={<>
        {existing && !existing.isSelf && <button className="btn" onClick={() => void remove()}><I k="trash" /> Remove</button>}
        <div className="grow" />
        <button className="btn" onClick={() => set({ drawer: null })}>Cancel</button>
        <button className="btn primary" onClick={() => void save()}><I k="check" /> {existing ? 'Save' : 'Add entity'}</button>
      </>}>
      {typeGrid}
      <div className="field"><label>Name</label>
        <input className="input" value={name} placeholder="e.g. Helios Energy" onChange={ev => setName(ev.target.value)} /></div>
      <div className="field"><label>What is this entity? <span className="muted" style={{ textTransform: 'none', fontWeight: 400 }}>— free text</span></label>
        <textarea className="textarea" value={desc} placeholder="Describe the entity in your own words…" onChange={ev => setDesc(ev.target.value)} /></div>

      <div className="section-h">Structured profile <span style={{ textTransform: 'none', color: 'var(--ink-3)', fontWeight: 400 }}>— drives the model</span></div>
      <div className="def-list">
        <div className="def-row"><span className="dt">Sector</span><span className="dd">
          <select className="select sm" value={profile.sector ?? existing?.profile.sector ?? 'Industrial manufacturing'}
            onChange={ev => setProfile(p => ({ ...p, sector: ev.target.value }))}>
            {['Industrial manufacturing', 'Energy', 'Consumer goods', 'Agriculture & food', 'Services & software', 'Logistics',
              'Public administration', 'Environmental advocacy', 'Knowledge worker / household'].map(x => <option key={x}>{x}</option>)}
          </select></span></div>
        {lvl('resourceDep', 'Resource dependence', 'share of costs exposed to raw inputs')}
        {lvl('footprint', 'Ecological footprint', 'pollution & regulatory exposure')}
        {lvl('adaptive', 'Adaptive capacity', 'speed of consolidation & learning')}
      </div>

      {existing && <PublicDataSection entity={existing} />}
    </Drawer>
  );
}
